import Axios from "axios";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { api_url } from "../../../config";
import { estVisible } from "./raccourcis";
import { resumeAnomalies, resumeEpsDuJour, resumeInvitations, resumeReorientations } from "./indicateurs";

// ============================================================
// useIndicateursDuJour — compteurs du tableau de bord. Un indicateur n'est
// chargé que si l'entrée de menu correspondante est visible pour l'agent
// (même filtrage que le menu), et ses appels passent par Axios sans passer
// par dataService : un compteur indisponible s'affiche « — », il ne toaste
// pas. Les appels sont ceux des écrans visés (eps/search, eps/mes-invitations,
// anomalies/mine, reorientations/search).
// ============================================================

const get = (url) => Axios.get(api_url + url).then((r) => r.data);
const post = (url, body) => Axios.post(api_url + url, body).then((r) => r.data);

export const INDICATEURS = [
  {
    id: "ep-du-jour",
    path: "/ep",
    libelle: "EP aujourd'hui",
    icone: "street-view",
    charger: (maintenant) =>
      Promise.all([post("eps/search", { year: maintenant.year }), get("sectors")]).then(([eps, sectors]) =>
        resumeEpsDuJour(eps, sectors, maintenant)
      ),
  },
  {
    id: "invitations-ep",
    path: "/myeps",
    libelle: "Invitations EP à répondre",
    icone: "reply",
    charger: (maintenant) => get("eps/mes-invitations").then((invitations) => resumeInvitations(invitations, maintenant)),
  },
  {
    id: "mes-anomalies",
    path: "/my-anomalies",
    libelle: "Anomalies à traiter",
    icone: "user-check",
    charger: () => get("anomalies/mine").then(resumeAnomalies),
  },
  {
    id: "reorientations",
    path: "/reorientations",
    libelle: "Réorientations en cours",
    icone: "map-signs",
    charger: () =>
      post("reorientations/search", {
        filters: { isFinished: false, fromSites: [], toSites: [] },
        page: 1,
        nbItemsByPage: 1,
      }).then(resumeReorientations),
  },
];

export default function useIndicateursDuJour(sections, { indicateurs = INDICATEURS, maintenant } = {}) {
  const visibles = useMemo(
    () => indicateurs.filter((ind) => estVisible(sections, ind.path)),
    [sections, indicateurs]
  );
  const cle = visibles.map((ind) => ind.id).join("|");
  const [etats, setEtats] = useState({});

  useEffect(() => {
    let annule = false;
    const instant = maintenant || DateTime.local();
    visibles.forEach((ind) => {
      setEtats((e) => ({ ...e, [ind.id]: { statut: "chargement" } }));
      ind.charger(instant).then(
        (resume) => {
          if (!annule) setEtats((e) => ({ ...e, [ind.id]: { statut: "ok", ...resume } }));
        },
        () => {
          if (!annule) setEtats((e) => ({ ...e, [ind.id]: { statut: "erreur" } }));
        }
      );
    });
    return () => {
      annule = true;
    };
    // Rechargé quand la liste des indicateurs visibles change (rôle, menu).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cle]);

  return visibles.map((ind) => ({ ...ind, ...(etats[ind.id] || { statut: "chargement" }) }));
}
